import React, { useState, useEffect, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ConfigContext from '../../contexts/ConfigContext';

import styles from './FloatingCta.module.postcss';

// Pages where the floating CTA should NOT appear
const HIDDEN_PATHS = [
  '/catering/register',
  '/catering',
  '/login',
  '/vendor/register',
  '/choose-account',
  '/forgot-password',
  '/privacy-policy',
  '/terms-and-conditions',
  '/error',
  '/not-found',
  '/deletion-status',
];

function getCtaText(pathname, vehicleType) {
  if (pathname.startsWith('/food-truck-catering')) {
    return `Book a food ${vehicleType} for your next event`;
  }
  if (pathname.startsWith('/food-trucks/')) {
    return `Book this food ${vehicleType} for your next event`;
  }
  if (pathname.startsWith('/services') || pathname === '/support') {
    return `Get free quotes from top food ${vehicleType}s`;
  }
  if (pathname.startsWith('/category')) {
    return `Book your favorite food ${vehicleType} for catering`;
  }
  if (pathname.startsWith('/blog')) {
    return `Hungry? Book a food ${vehicleType} for your next event`;
  }
  if (pathname === '/about' || pathname === '/vendors') {
    return `Join the best food ${vehicleType} platform in the area`;
  }
  return `Book a food ${vehicleType} for your next event`;
}

function FloatingCtaInner() {
  const { vehicle_type } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const isHidden = HIDDEN_PATHS.some(
    (path) => location.pathname === path || location.pathname.startsWith(path + '/')
  );

  useEffect(() => {
    // Reset dismissed state on route change
    setDismissed(false);
    setVisible(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isHidden || dismissed) return;

    const handleScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      const threshold = 400;
      setVisible(scrollY > threshold);
    };

    // Check initial scroll position
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHidden, dismissed, location.pathname]);

  if (isHidden || dismissed) return null;

  const ctaText = getCtaText(location.pathname, vehicleType);
  const isTruckPage = location.pathname.startsWith('/food-trucks/');

  return (
    <div
      className={`${styles.FloatingCta} ${visible ? styles.FloatingCta__visible : ''} ${isTruckPage ? styles.FloatingCta__mobileOnly : ''}`}
      role='complementary'
      aria-label='Book catering'
    >
      <div className={styles.FloatingCta_content}>
        <h4>{ctaText}</h4>
        <p>Free no-obligation quotes from local food {vehicleType}s</p>
      </div>
      <div className={styles.FloatingCta_button}>
        <Link to='/catering/register' className='Button'>
          Get Started
        </Link>
      </div>
      <button
        className={styles.FloatingCta_close}
        onClick={() => setDismissed(true)}
        aria-label='Dismiss'
        type='button'
      >
        ×
      </button>
    </div>
  );
}

// Client-only wrapper — renders nothing during SSR to avoid hydration issues
function FloatingCta() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  return <FloatingCtaInner />;
}

export default FloatingCta;
